import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { KTCard, KTCardBody, KTIcon } from "../../../_metronic/helpers";
import { PageTitle } from "../../../_metronic/layout/core";
import { DataTable } from "../../../_metronic/partials/common/DataTable";
import { useAuth } from "../../modules/auth";
import { useMGNREGSSubmissions } from "./hooks/useSchemeDataEntry";

const SchemeDataEntryList = () => {
	const { currentUser } = useAuth();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const statusParam = searchParams.get("status")?.toLowerCase();

	const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

	const { submissions = [], isLoading, totalItems, totalPages } =
		useMGNREGSSubmissions(pagination.pageIndex + 1, pagination.pageSize);

	const filteredSubmissions = useMemo(() => {
		if (!submissions || !Array.isArray(submissions)) return [];
		if (!statusParam) return submissions;
		return submissions.filter((item: any) => {
			const itemStatus = (item.status || item.stage || "completed").toLowerCase();
			if (statusParam === "pending")
				return itemStatus === "pending" || itemStatus === "draft";
			if (statusParam === "inprogress")
				return itemStatus === "inprogress" || itemStatus === "in-progress" || itemStatus === "submitted";
			if (statusParam === "completed")
				return itemStatus === "completed" || itemStatus === "approved" || !item.status;
			return true;
		});
	}, [submissions, statusParam]);

	const userRoleId = Number(currentUser?.role_id);
	if (![1, 2, 3, 4].includes(userRoleId)) {
		return <Navigate to="/dashboard" />;
	}

	const columns = useMemo<ColumnDef<any>[]>(
		() => [
			{
				header: "S.No",
				cell: (info) => {
					const pageIndex = info.table.getState().pagination.pageIndex;
					const pageSize = info.table.getState().pagination.pageSize;
					return pageIndex * pageSize + info.row.index + 1;
				},
				meta: { align: "center" },
			},
			{
				header: "Scheme",
				cell: (info) => {
					const item = info.row.original;
					return (
						<div className="d-flex flex-column align-items-center text-center">
							<span className="text-dark fw-bold fs-6">
								{item.scheme_name || "MGNREGS"}
							</span>
							{item.sub_scheme_name && (
								<span className="text-primary fw-semibold fs-7">
									{item.sub_scheme_name}
								</span>
							)}
							{(item.district_name || item.block_name) && (
								<span className="text-muted fw-semibold fs-7">
									{[item.district_name, item.block_name]
										.filter(Boolean)
										.join(" / ")}
								</span>
							)}
						</div>
					);
				},
				meta: { align: "center" },
			},
			{
				accessorKey: "brp_name",
				header: "BRP name",
				cell: (info) => (
					<span className="text-dark fw-bold fs-6">
						{String(info.getValue() || "-")}
					</span>
				),
				meta: { align: "center" },
			},
			{
				accessorKey: "brp_employee_code",
				header: "Employee Code",
				cell: (info) => (
					<span className="text-dark fw-bold fs-6">
						{String(info.getValue() || "-")}
					</span>
				),
				meta: { align: "center" },
			},
			{
				accessorKey: "audit_start_date",
				header: "Audit Start date",
				cell: (info) => (
					<span className="text-dark fw-bold fs-6">
						{String(info.getValue() || "-")}
					</span>
				),
				meta: { align: "center" },
			},
			{
				accessorKey: "audit_end_date",
				header: "audit end date",
				cell: (info) => (
					<span className="text-dark fw-bold fs-6">
						{String(info.getValue() || "-")}
					</span>
				),
				meta: { align: "center" },
			},
			{
				id: "action",
				header: "action",
				cell: ({ row }) => {
					const item = row.original;
					return (
						<div className="d-flex justify-content-center">
							<button
								onClick={() => navigate(`/scheme-data-entry/view/${item.id}`)}
								className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1"
								title="View"
							>
								<KTIcon iconName="eye" className="fs-3" />
							</button>
							{[1, 4].includes(userRoleId) && (
								<button
									onClick={() => navigate(`/scheme-data-entry/edit/${item.id}`)}
									className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm"
									title="Edit"
								>
									<KTIcon iconName="pencil" className="fs-3" />
								</button>
							)}
						</div>
					);
				},
				meta: { align: "center" },
			},
		],
		[navigate, userRoleId],
	);

	return (
		<>
			<PageTitle>Scheme Data Entry</PageTitle>

			<KTCard>
				<div className="card-header border-0 pt-5 align-items-center">
					<h3 className="card-title align-items-start flex-column">
						<span className="card-label fw-bold fs-3 mb-1">
							Scheme Data Entry List
						</span>
					</h3>
					<div className="card-toolbar align-items-center">
						{userRoleId === 4 && (
							<Link
								to="/scheme-data-entry/add"
								className="btn btn-sm btn-primary"
							>
								<KTIcon iconName="plus" className="fs-2" />
								Add Scheme Entry
							</Link>
						)}
					</div>
				</div>
				<KTCardBody className="py-4">
					{statusParam && (
						<div className="mb-5 p-3 rounded bg-light-primary border border-primary border-opacity-25 d-flex align-items-center justify-content-between">
							<div className="d-flex align-items-center gap-2">
								<span className="badge badge-primary fs-8 text-capitalize">
									{statusParam} Filter
								</span>
								<span className="fs-7 fw-bold text-primary text-capitalize">
									Showing <strong>{statusParam}</strong> scheme entries ({filteredSubmissions.length} found)
								</span>
							</div>
							<button
								type="button"
								className="btn btn-sm btn-light-primary py-1 px-3 fs-8 fw-bold"
								onClick={() => setSearchParams({})}
							>
								Clear Filter ×
							</button>
						</div>
					)}

					<DataTable
						data={filteredSubmissions}
						columns={columns}
						isLoading={isLoading}
						manualPagination={true}
						totalCount={statusParam ? filteredSubmissions.length : totalItems}
						pageCount={statusParam ? Math.ceil(filteredSubmissions.length / pagination.pageSize) : totalPages}
						onPaginationChange={setPagination}
						currentPageIndex={pagination.pageIndex}
						currentPageSize={pagination.pageSize}
					/>
				</KTCardBody>
			</KTCard>
		</>
	);
};

export default SchemeDataEntryList;
